
import React from "react";
import { Check } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProgramCreation } from "@/contexts/ProgramCreationContext";
import { TierFormData } from "@/contexts/program-creation/types";
import ClientInformationSection from "./review/ClientInformationSection";
import ProgramDetailsSection from "./review/ProgramDetailsSection";
import TierSpecificSection from "./review/TierSpecificSection";
import PreferencesSection from "./review/PreferencesSection";
import ReviewFormFooter from "./review/ReviewFormFooter";

type ProgramReviewFormProps = {
  onSubmit: (data: TierFormData) => Promise<void>;
  onBack: () => void;
  isSaving: boolean;
};

export default function ProgramReviewForm({
  onSubmit,
  onBack,
  isSaving
}: ProgramReviewFormProps) {
  const { selectedClient } = useProgramCreation();
  
  // Get program data from localStorage
  const programDataStr = localStorage.getItem('programData');
  const programData = programDataStr ? JSON.parse(programDataStr) : {};
  
  // Get tier data from localStorage
  const tierDataStr = localStorage.getItem('tierData');
  const tierData = tierDataStr ? JSON.parse(tierDataStr) : {};
  
  // Get preferences data from localStorage
  const preferencesDataStr = localStorage.getItem('preferencesData');
  const preferencesData = preferencesDataStr ? JSON.parse(preferencesDataStr) : {};

  // Create a handler for the button click
  const handleSubmit = () => {
    // Call onSubmit with the tier data from localStorage
    onSubmit(tierData);
  };

  return (
    <Card className="backdrop-blur-lg bg-white/10 border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Check className="h-5 w-5" />
          Review Your Program
        </CardTitle>
        <CardDescription className="text-white/70">
          Please review all details before finalizing your program
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-8">
            {/* Client Information */}
            <ClientInformationSection selectedClient={selectedClient} />

            {/* Program Details */}
            <ProgramDetailsSection programData={programData} />

            {/* Tier-Specific Information */}
            <TierSpecificSection 
              tier={programData?.tier} 
              tierData={tierData} 
            />

            {/* Preferences & Customizations */}
            <PreferencesSection preferencesData={preferencesData} />
          </div>
        </ScrollArea>
      </CardContent>
      <CardFooter>
        <ReviewFormFooter 
          onBack={onBack} 
          onSubmit={handleSubmit} 
          isSaving={isSaving} 
        />
      </CardFooter>
    </Card>
  );
}
